import { useContext, useState, useEffect } from 'react';
import { UpdateCompanyClientData } from '@esg/syntiro-api-core';
import { Drawer, Grid, Theme, Tooltip, useTheme } from '@mui/material';
import { QuotaUsageKey, Subscription } from '@esg/esg-global-types';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridValueFormatterParams
} from '@mui/x-data-grid';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import {
  default_subscription_key_values,
  getCompanySubscriptionFromMemory,
  subscription_label
} from '../../../lib/app/subscription';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { snakeCaseToTitleCase } from '../../../util/general_utils';
import { CompanyExtended } from '../../../lib/app/company';
import { useSubscriptionContext } from '../../../context/SubscriptionContext';
import CompanySelect from '../../shared/input/select/CompanySelect';
import { CompanyContext } from '../../../context/CompanyContext';
import {
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FEEDBACK_UNKNOWN_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../../util/global_variables';
import { updateCompany } from '../../../graphql/services/company.service';

export type SubscriptionGridRow = {
  id: string;
  quota: string | null;
  usage: string;
};

const SOURCE = 'PanelSubscriptions.tsx';

/**
 * Datagrid handling the display of master list Subscriptions
 * @returns {JSX.Element}
 */
const PanelSubscriptions = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const { company } = useContext(CompanyContext);
  const [actionRow, setActionRow] = useState<SubscriptionGridRow | null>(null);
  const [subscriptionRows, setSubscriptionRows] = useState<Array<SubscriptionGridRow>>([]);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState<boolean>(false);
  const [gridLoading, setGridLoading] = useState<boolean>(true);
  const [selectedCompany, setSelectedCompany] = useState<CompanyExtended | null>(null);

  const theme: Theme = useTheme();
  const primary_blue: string = theme.palette.primary.main;

  const { setSubscriptionQuota } = useSubscriptionContext();

  const handleChangeCompany = (new_company: CompanyExtended) => {
    setSelectedCompany(new_company);
  };

  // Handler functions
  const handleEditClick = (subscription: SubscriptionGridRow): void => {
    setActionRow(subscription);
    setDisplayWidgetPanelRight(true);
  };

  const handleUpdateRows = (rows_data: Array<SubscriptionGridRow>): void => {
    setSubscriptionRows(rows_data);
  };

  const subscriptionKeyLabels: Partial<Record<QuotaUsageKey, string>> = {
    monthly_data_exports: 'Monthly Data Downloads'
  };

  const columns: Array<GridColDef> = [
    {
      field: 'id',
      headerName: 'Entity',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      valueFormatter: (params: GridValueFormatterParams<string>) => {
        if (params.value == null) return 0;
        return (
          subscriptionKeyLabels[params.value as QuotaUsageKey] ?? snakeCaseToTitleCase(params.value)
        );
      }
    },
    {
      field: 'quota',
      headerName: 'Quota',
      headerAlign: 'left',
      align: 'left',
      flex: 1,
      renderCell: ({ value }) => {
        if (value == null) {
          return (
            <Tooltip title="No quota set">
              <span>–</span>
            </Tooltip>
          );
        }
        return value;
      }
    },
    {
      field: 'usage',
      headerName: 'Usage',
      headerAlign: 'left',
      type: 'number',
      align: 'left',
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit subscription">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={row.locked}
            />
          </>
        ];
      }
    }
  ];

  const edit_inputs: EditInput[] = [
    {
      id: 'quota',
      type: 'number',
      label: 'Quota',
      helperText: `Quota cannot be lower than current usage: ${actionRow?.usage ?? 0}`,
      textValidation: (input_value: unknown) => {
        const quota_number = Number(input_value);
        const usage_number = Number(actionRow?.usage ?? 0);
        return !isNaN(quota_number) && !isNaN(usage_number) && quota_number >= usage_number;
      }
    }
  ];

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const subscriptions: Array<SubscriptionGridRow> =
        selectedCompany && selectedCompany.subscription
          ? getCompanySubscriptionFromMemory(selectedCompany.subscription)
          : [];
      // Load rows to memory.
      setSubscriptionRows(subscriptions);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to fetch subscriptions', tracking_id),
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        setSubscriptionRows([]);
        fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_UNKNOWN_FAILURE(tracking_id),
        state: true,
        type: 'error'
      });
    }
    return;
  }, [selectedCompany]);

  const updateRow = async (
    updated_row: SubscriptionGridRow,
    original_data: SubscriptionGridRow
  ): Promise<void> => {
    try {
      if (!selectedCompany) {
        throw new Error('No company selected.');
      }

      const quota_key: QuotaUsageKey = updated_row.id as QuotaUsageKey;
      const group_id: string = selectedCompany.group.id;

      // Ensure subscription, quota, and usage objects exist
      const subscription: Subscription = selectedCompany?.subscription ?? {
        quota: default_subscription_key_values,
        usage: default_subscription_key_values
      };

      const quota: Partial<Record<QuotaUsageKey, number>> = subscription.quota;
      const usage: Partial<Record<QuotaUsageKey, number>> = subscription.usage;

      if (updated_row.quota == null) {
        throw new Error('Quota value is required.');
      }
      const newQuota: number = parseInt(updated_row.quota);

      if (selectedCompany.id === company?.id) {
        setSubscriptionQuota({
          [quota_key]: newQuota
        } as Record<QuotaUsageKey, number>);
      }

      const updated_subscription: Subscription = {
        ...subscription,
        quota: {
          ...quota,
          [quota_key]: newQuota
        },
        usage: {
          ...usage,
          [quota_key]: parseInt(updated_row.usage)
        }
      };

      const updated_company: CompanyExtended = {
        ...selectedCompany,
        subscription: updated_subscription
      };

      const update_company_client_data: UpdateCompanyClientData = {
        id: selectedCompany.id,
        subscription: updated_subscription
      };
      await updateCompany(
        {
          group_id
        },
        update_company_client_data
      ).then(() => {
        setSelectedCompany(updated_company);
        handleUpdateRows(
          subscriptionRows.map((row: SubscriptionGridRow) =>
            updated_row.id === row.id ? { ...updated_row } : row
          )
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE('Unable to update subscription', tracking_id),
        state: true,
        type: 'error'
      });
    }
  };

  return (
    <>
      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => setDisplayWidgetPanelRight(false)}
      >
        {/* Edit Widget */}
        <ConfigEditWidget
          widget_label={subscription_label.one}
          icon={<EditIcon width={40} fill={primary_blue} />}
          handleClose={() => setDisplayWidgetPanelRight(false)}
          edit_entity={actionRow}
          inputs={edit_inputs}
          handleEditInput={(input_id: string, value: unknown) =>
            actionRow && setActionRow({ ...actionRow, [input_id]: value })
          }
          confirmEditFunction={(
            updated_subscription: SubscriptionGridRow,
            original_subscription: SubscriptionGridRow
          ) => updateRow(updated_subscription, original_subscription)}
        />
      </Drawer>

      <Grid container spacing={8}>
        <Grid size={6}>
          <CompanySelect
            group_by="group.name"
            handleChangeCompanies={(company: CompanyExtended | Array<CompanyExtended> | null) => {
              company && !Array.isArray(company) && handleChangeCompany(company);
            }}
          />
        </Grid>

        {/* Interactive Data Table */}
        <Grid size={12}>
          <DataGrid
            autoHeight
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 }
              },
              sorting: {
                sortModel: [{ field: 'modified', sort: 'desc' }]
              }
            }}
            hideFooter={subscriptionRows.length > 1 ? false : true}
            columnHeaderHeight={subscriptionRows.length < 1 ? 0 : 56}
            pageSizeOptions={[10, 25, 50, 100]}
            loading={gridLoading}
            rows={subscriptionRows}
            columns={columns}
            disableRowSelectionOnClick
            slots={{
              toolbar: () => (
                <DataGridToolbar
                  disable_export={subscriptionRows.length === 0}
                  entity_label={subscription_label}
                />
              ),
              noRowsOverlay: () => <DataGridNoRows missing_label={subscription_label} />,
              loadingOverlay: () => <DataGridLoading columns={columns.length} />
            }}
            sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default PanelSubscriptions;
